'use client';

import type { ApolloError } from '@apollo/client';

import OopsIcon from '@/assets/icons/shared/oops';
import AuthSessionErrorPanel from '@/components/auth/AuthSessionErrorPanel';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  NETWORK_RECOVERY_ERROR_MESSAGE,
  SESSION_VALIDATION_ERROR_MESSAGE,
  getSafeErrorMessage,
} from '@/lib/auth/sessionErrors';
import { extractGraphQLError } from '@/lib/utils/extractGraphQLError';

type LoadingAndErrorProps = {
  loading?: boolean | null;
  error?: ApolloError | string | null;
  onButtonClick?: () => void;
  buttonLabel?: string;
};

const LoadingAndError: React.FC<LoadingAndErrorProps> = ({ loading, error, onButtonClick, buttonLabel }) => {
  const errorMessage = typeof error === 'string' ? error : (extractGraphQLError(error) ?? error?.message);
  const displayErrorMessage = getSafeErrorMessage(errorMessage);
  const isAuthRecoveryError =
    displayErrorMessage === SESSION_VALIDATION_ERROR_MESSAGE || displayErrorMessage === NETWORK_RECOVERY_ERROR_MESSAGE;

  if (!loading && error && isAuthRecoveryError) {
    return (
      <AuthSessionErrorPanel
        buttonLabel={buttonLabel}
        description={displayErrorMessage}
        onSignOutClick={onButtonClick}
      />
    );
  }

  return (
    <div className="flex flex-col w-full h-[75vh] items-center justify-center space-y-10 m-auto">
      {loading ? (
        <LoadingSpinner size={40} />
      ) : error ? (
        <>
          <div className="flex space-x-2 w-full justify-center">
            <OopsIcon />
          </div>
          <div className="space-y-2 text-center">
            <h1 className="text-2xl text-alertFg font-bold max-w-[80vw] lg:max-w-[50vw]">Something went wrong!</h1>
            <p className="text-xl text-alertFg font-bold max-w-[80vw] lg:max-w-[50vw]">{displayErrorMessage}</p>
          </div>
          {onButtonClick && (
            <Button variant="primary" onClick={onButtonClick}>
              {buttonLabel ?? 'navigate'}
            </Button>
          )}
        </>
      ) : null}
    </div>
  );
};

export default LoadingAndError;
